(() => {
  if (window.RDCProductUpsellPopupLoaded) return;
  window.RDCProductUpsellPopupLoaded = true;

  const STORAGE_KEY = 'rdcProductUpsellPopup';
  const CART_ADD_RE = /\/cart\/add(\.js)?/;
  const root = window.Shopify?.routes?.root || '/';

  /*
    TEMP TESTING SETTINGS:
    Change these to false when ready for production.
  */
  const TEMP_FORCE_OPEN_WITHOUT_TRIGGER = false;
  const TEMP_DISABLE_TIMER = false;
  const TEMP_IGNORE_LOCAL_STORAGE = false;

  const money = (cents, format) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);
  };

  const readStorage = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  };

  const writeStorage = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {}
  };

  const markOffer = (productId, status) => {
    if (TEMP_IGNORE_LOCAL_STORAGE) return;

    const data = readStorage();
    data[String(productId)] = {
      status,
      timestamp: Date.now()
    };
    writeStorage(data);
  };

  const hasSeenOffer = (productId) => {
    if (TEMP_IGNORE_LOCAL_STORAGE) return false;
    return Boolean(readStorage()[String(productId)]);
  };

  class ProductUpsellPopup {
    constructor(rootEl) {
      this.root = rootEl;
      this.dialog = rootEl.querySelector('[data-upsell-dialog]');
      this.productData = JSON.parse(rootEl.querySelector('[data-upsell-product-json]').textContent);
      this.productId = String(rootEl.dataset.upsellProductId);
      this.discountCode = rootEl.dataset.discountCode || 'GRABIT';
      this.discountPercent = Number(rootEl.dataset.discountPercent || 20);
      this.timerSeconds = Number(rootEl.dataset.timerSeconds || 120);
      this.overlayCloses = rootEl.dataset.overlayClose === 'true';

      this.mainImage = rootEl.querySelector('[data-upsell-main-image]');
      this.mainImagePlaceholder = rootEl.querySelector('[data-upsell-main-image-placeholder]');
      this.optionsEl = rootEl.querySelector('[data-upsell-options]');
      this.addButton = rootEl.querySelector('[data-upsell-add]');
      this.declineButton = rootEl.querySelector('[data-upsell-decline]');
      this.errorEl = rootEl.querySelector('[data-upsell-error]');
      this.availabilityEl = rootEl.querySelector('[data-upsell-availability]');
      this.timerEl = rootEl.querySelector('[data-upsell-timer]');
      this.salePriceEl = rootEl.querySelector('[data-upsell-sale-price]');
      this.comparePriceEl = rootEl.querySelector('[data-upsell-compare-price]');

      this.selectedOptions = {};
      this.selectedVariant = null;
      this.interval = null;
      this.active = false;
      this.processing = false;
      this.lastFocus = null;

      this.bind();
      this.selectInitialVariant();
      this.renderOptions();
      this.updateVariantState();
    }

    bind() {
      this.addButton.addEventListener('click', () => this.accept());
      this.declineButton.addEventListener('click', () => this.close('declined', true));

      this.root.querySelector('[data-upsell-close]')?.addEventListener('click', () => {
        this.close('declined', true);
      });

      this.root.querySelector('[data-upsell-overlay]')?.addEventListener('click', () => {
        if (this.overlayCloses) this.close('declined', true);
      });

      document.addEventListener('keydown', (event) => {
        if (!this.active) return;

        if (event.key === 'Escape') this.close('declined', true);
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    renderOptions() {
      if (!this.productData.options?.length || !this.productData.variants?.length) return;

      this.productData.options.forEach((option, optionIndex) => {
        const isColor = this.isColorOption(option.name);
        const wrapper = document.createElement('div');
        wrapper.className = 'product-upsell-popup__option';

        const label = document.createElement('label');
        label.className = 'product-upsell-popup__option-label';
        label.textContent = option.name;
        label.id = `UpsellOptionLabel-${this.productId}-${optionIndex}`;
        wrapper.appendChild(label);

        if (isColor) {
          const values = document.createElement('div');
          values.className = 'product-upsell-popup__option-values';
          values.setAttribute('role', 'radiogroup');
          values.setAttribute('aria-labelledby', label.id);

          option.values.forEach((value) => {
            const button = document.createElement('button');
            const image = this.findVariantImage(optionIndex, value, true);

            button.type = 'button';
            button.className = 'product-upsell-popup__swatch';
            button.dataset.optionName = option.name;
            button.dataset.optionValue = value;
            button.setAttribute('role', 'radio');
            button.setAttribute('aria-label', `${option.name}: ${value}`);

            if (image) {
              button.innerHTML = `<img src="${image}" alt="">`;
            } else {
              button.innerHTML = `<span class="product-upsell-popup__swatch-fallback">${this.shortLabel(value)}</span>`;
            }

            button.addEventListener('click', () => {
              if (button.disabled) return;

              this.selectedOptions[option.name] = value;

              const previewImage = this.findVariantImage(optionIndex, value, false);
              if (previewImage) {
                this.setMainImage(previewImage, `${this.productData.title} - ${value}`);
              }

              this.updateVariantState();
            });

            values.appendChild(button);
          });

          wrapper.appendChild(values);
        } else {
          const select = document.createElement('select');
          select.className = 'product-upsell-popup__select';
          select.dataset.optionName = option.name;
          select.setAttribute('aria-labelledby', label.id);

          option.values.forEach((value) => {
            const optionEl = document.createElement('option');
            optionEl.value = value;
            optionEl.textContent = value;
            select.appendChild(optionEl);
          });

          select.addEventListener('change', () => {
            this.selectedOptions[option.name] = select.value;
            this.updateVariantState();
          });

          wrapper.appendChild(select);
        }

        this.optionsEl.appendChild(wrapper);
      });
    }

    isColorOption(name) {
      return /colou?r|shade|tone/i.test(name);
    }

    shortLabel(value) {
      return String(value).trim().slice(0, 2).toUpperCase();
    }

    selectInitialVariant() {
      const variant = this.productData.variants.find((item) => item.available) || this.productData.variants[0];
      if (!variant) return;

      this.productData.options.forEach((option, index) => {
        this.selectedOptions[option.name] = variant[`option${index + 1}`];
      });
    }

    getVariantImageData(variant) {
      if (!variant) return null;

      const liquidImage = this.productData.variantImages?.[String(variant.id)];
      if (liquidImage?.src) return liquidImage;

      const src =
        variant?.featured_image?.src ||
        variant?.featured_image?.url ||
        variant?.featured_media?.preview_image?.src ||
        variant?.image?.src ||
        variant?.image ||
        '';

      if (!src) return null;

      return {
        src,
        swatch: src,
        alt: variant?.featured_image?.alt || this.productData.title
      };
    }

    getVariantImageSrc(variant) {
      return this.getVariantImageData(variant)?.src || '';
    }

    findVariantImage(optionIndex, value, swatch = false) {
      const key = `option${optionIndex + 1}`;

      const exactVariant = this.productData.variants.find((variant) => {
        const matchesClickedOption = variant[key] === value;
        const matchesOtherOptions = this.productData.options.every((option, index) => {
          if (index === optionIndex) return true;
          return variant[`option${index + 1}`] === this.selectedOptions[option.name];
        });

        return matchesClickedOption && matchesOtherOptions && this.getVariantImageSrc(variant);
      });

      const fallbackVariant =
        exactVariant ||
        this.productData.variants.find((variant) => {
          return variant[key] === value && this.getVariantImageSrc(variant);
        });

      const image = this.getVariantImageData(fallbackVariant);
      if (!image) return '';

      return swatch ? image.swatch || image.src : image.src;
    }

    getSelectedVariant() {
      return this.productData.variants.find((variant) => {
        return this.productData.options.every((option, index) => {
          return variant[`option${index + 1}`] === this.selectedOptions[option.name];
        });
      });
    }

    optionValueAvailable(optionName, value) {
      const testOptions = {
        ...this.selectedOptions,
        [optionName]: value
      };

      return this.productData.variants.some((variant) => {
        const matches = this.productData.options.every((option, index) => {
          return variant[`option${index + 1}`] === testOptions[option.name];
        });

        return matches && variant.available;
      });
    }

    updateVariantState() {
      this.root.querySelectorAll('.product-upsell-popup__swatch[data-option-name]').forEach((button) => {
        const selected = this.selectedOptions[button.dataset.optionName] === button.dataset.optionValue;

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.disabled = !this.optionValueAvailable(button.dataset.optionName, button.dataset.optionValue);
      });

      this.root.querySelectorAll('.product-upsell-popup__select[data-option-name]').forEach((select) => {
        select.value = this.selectedOptions[select.dataset.optionName];

        Array.from(select.options).forEach((optionEl) => {
          optionEl.disabled = !this.optionValueAvailable(select.dataset.optionName, optionEl.value);
        });
      });

      this.selectedVariant = this.getSelectedVariant();
      const valid = Boolean(this.selectedVariant && this.selectedVariant.available);
      const selectedVariantImage = this.getVariantImageData(this.selectedVariant);

      if (selectedVariantImage?.src) {
        this.setMainImage(selectedVariantImage.src, selectedVariantImage.alt || this.productData.title);
      }

      if (this.selectedVariant) {
        const originalPrice = Number(this.selectedVariant.price);
        const discountedPrice = Math.round(originalPrice * ((100 - this.discountPercent) / 100));

        this.comparePriceEl.textContent = money(originalPrice, this.productData.moneyFormat);
        this.salePriceEl.textContent = money(discountedPrice, this.productData.moneyFormat);
      }

      this.addButton.disabled = !valid;
      this.availabilityEl.textContent = valid ? '' : this.productData.unavailableMessage;
    }

    setMainImage(src, alt) {
      if (!src) return;

      if (this.mainImage) {
        this.mainImage.removeAttribute('srcset');
        this.mainImage.removeAttribute('sizes');
        this.mainImage.src = src;
        this.mainImage.alt = alt || this.productData.title;
      }

      if (this.mainImagePlaceholder) {
        this.mainImagePlaceholder.style.display = 'none';
      }
    }

    open(trigger) {
      if (this.active || hasSeenOffer(this.productId)) return false;

      this.lastFocus = trigger || document.activeElement;
      this.active = true;

      suppressCartDrawer();

      this.root.classList.add('is-active');
      this.dialog.hidden = false;
      this.dialog.focus();

      /*
        TEMP TESTING:
        Timer is disabled while styling/testing.
        Restore by setting TEMP_DISABLE_TIMER = false.
      */
      if (!TEMP_DISABLE_TIMER) {
        this.startTimer();
      }

      return true;
    }

    close(status, openDrawer) {
      if (!this.active) return;

      this.active = false;
      this.root.classList.remove('is-active');
      this.dialog.hidden = true;
      document.body.classList.remove('product-upsell-popup-lock');
      this.stopTimer();

      if (status) markOffer(this.productId, status);

      if (this.lastFocus?.focus) {
        this.lastFocus.focus({ preventScroll: true });
      }

      if (openDrawer) {
        openCartDrawer();
      }
    }

    startTimer() {
      this.stopTimer();

      const expiresAt = Date.now() + this.timerSeconds * 1000;

      const tick = () => {
        const remaining = Math.max(0, expiresAt - Date.now());
        const totalSeconds = Math.ceil(remaining / 1000);
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');

        if (this.timerEl) this.timerEl.textContent = `${minutes}:${seconds}`;

        if (remaining <= 0) {
          this.availabilityEl.textContent = this.productData.expiredMessage;
          this.close('expired', true);
        }
      };

      tick();
      this.interval = window.setInterval(tick, 1000);
    }

    stopTimer() {
      if (this.interval) window.clearInterval(this.interval);
      this.interval = null;
    }

    async accept() {
      if (this.processing || !this.selectedVariant?.available) return;

      this.processing = true;
      this.addButton.disabled = true;
      this.errorEl.textContent = '';

      try {
        await applyDiscount(this.discountCode);

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: String(this.selectedVariant.id),
            quantity: 1
          })
        });

        if (!response.ok) throw new Error('Cart add failed');

        markOffer(this.productId, 'accepted');
this.close(null, false);

await refreshCartDrawer();

window.setTimeout(() => {
  openCartDrawer();
}, 150);
      } catch {
        this.errorEl.textContent = this.productData.errorMessage;
        this.addButton.disabled = false;
      } finally {
        this.processing = false;
      }
    }

    trapFocus(event) {
      const focusable = this.dialog.querySelectorAll(
        'button:not(:disabled), select:not(:disabled), [href], input, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function suppressCartDrawer() {
    document.body.classList.add('product-upsell-popup-lock');

    document.querySelectorAll('#CartDrawer, cart-drawer, .cart-drawer, drawer-modal, .drawer').forEach((el) => {
      el.classList.remove('active', 'is-open', 'animate');
      el.removeAttribute('open');
      el.setAttribute('aria-hidden', 'true');

      if (typeof el.close === 'function') {
        try {
          el.close();
        } catch {}
      }
    });
  }

  async function applyDiscount(code) {
    if (!code) return;

    try {
      await fetch(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart`)}`, {
        credentials: 'same-origin'
      });
    } catch {}
  }

  async function refreshCartDrawer() {
    const drawer = document.getElementById('CartDrawer');

    if (drawer?.getSectionsToRender && drawer?.renderContents) {
      const sections = drawer.getSectionsToRender().map((section) => section.id).join(',');
      const response = await fetch(`${root}?sections=${sections}`);
      drawer.renderContents({ sections: await response.json() });
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  function openCartDrawer() {
    document.body.classList.remove('product-upsell-popup-lock');

    const drawer = document.getElementById('CartDrawer');

    if (drawer?.open && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id: 'CartDrawer' } }));

    if (drawer) {
      drawer.classList.add('active', 'is-open', 'animate');
      drawer.removeAttribute('hidden');
      drawer.removeAttribute('aria-hidden');
    }
  }

  function getPopup() {
    const el = document.querySelector('[data-product-upsell-popup]');
    return el?._productUpsellPopup || null;
  }

  function init() {
    document.querySelectorAll('[data-product-upsell-popup]').forEach((el) => {
      if (!el._productUpsellPopup) {
        el._productUpsellPopup = new ProductUpsellPopup(el);
      }
    });
  }

  if (!window.RDCProductUpsellOriginalFetch) {
    window.RDCProductUpsellOriginalFetch = window.fetch;
  }

  window.fetch = async function productUpsellFetch(input, initOptions = {}) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = String(initOptions?.method || input?.method || 'GET').toUpperCase();
    const isCartAdd = CART_ADD_RE.test(url) && method === 'POST';

    const response = await window.RDCProductUpsellOriginalFetch.apply(this, arguments);

    /*
      PRODUCTION TRIGGER:
      Used once TEMP_FORCE_OPEN_WITHOUT_TRIGGER is false.
    */
    if (!TEMP_FORCE_OPEN_WITHOUT_TRIGGER && isCartAdd && response.ok) {
      const popup = getPopup();

      if (popup && !popup.processing && !popup.active && !hasSeenOffer(popup.productId)) {
        window.requestAnimationFrame(() => {
          const opened = popup.open(document.activeElement);
          if (!opened) openCartDrawer();
        });
      }
    }

    return response;
  };

  document.addEventListener('DOMContentLoaded', () => {
    init();

    /*
      TEMP TESTING:
      Forces popup open without Add to Cart trigger.
      Turn off by setting TEMP_FORCE_OPEN_WITHOUT_TRIGGER = false.
    */
    if (TEMP_FORCE_OPEN_WITHOUT_TRIGGER) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}

      window.setTimeout(() => {
        const popup = getPopup();
        if (popup) popup.open(document.querySelector('.product-form__buttons button') || document.body);
      }, 300);
    }
  });

  document.addEventListener('shopify:section:load', () => {
    init();

    if (TEMP_FORCE_OPEN_WITHOUT_TRIGGER) {
      window.setTimeout(() => {
        const popup = getPopup();
        if (popup) popup.open(document.querySelector('.product-form__buttons button') || document.body);
      }, 300);
    }
  });
})();

//Blog Posts

(function () {
  function normalizeHeading(value) {
    return (value || '').trim().replace(/\s+/g, ' ');
  }

  function anchorId(value) {
    return normalizeHeading(value).replace(/\s/g, '_');
  }

  document.querySelectorAll('[data-rdc-article]').forEach(function (article) {
    var content = article.querySelector('[data-rdc-article-content]');
    var toc = article.querySelector('[data-rdc-toc]');

    if (content && toc) {
      var headings = Array.prototype.slice.call(content.querySelectorAll('h2, h3'));

      toc.querySelectorAll('[data-rdc-toc-item]').forEach(function (link) {
        var tocText = normalizeHeading(link.getAttribute('data-rdc-toc-item'));
        var id = anchorId(tocText);
        var target = headings.find(function (heading) {
          return normalizeHeading(heading.textContent) === tocText;
        });

        if (target) {
          target.id = id;
          link.setAttribute('href', '#' + id);
        } else {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
        }
      });
    }

    article.querySelectorAll('[data-rdc-faq]').forEach(function (faq) {
      faq.querySelectorAll('.rdc-article-faq-button').forEach(function (button) {
        button.addEventListener('click', function () {
          var panel = document.getElementById(button.getAttribute('aria-controls'));
          var expanded = button.getAttribute('aria-expanded') === 'true';

          button.setAttribute('aria-expanded', String(!expanded));
          if (panel) {
            panel.hidden = expanded;
          }
        });
      });
    });
  });
})();
